import { memo } from 'react'
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material'
import {
  School as SchoolIcon,
  VideoLibrary as VideoIcon,
  Business as BusinessIcon,
  Event as EventIcon,
} from '@mui/icons-material'

interface CertificateCounts {
  recorded: number
  live: number
  external: number
  event: number
}

interface LearningSummaryCardProps {
  certificateCounts: CertificateCounts
  selectedUserCertificatesStatus: string
}

export let LearningSummaryCard = memo(
  ({
    certificateCounts,
    selectedUserCertificatesStatus,
  }: LearningSummaryCardProps) => {
    let CertificateCountDisplay = ({
      count,
      isLoading,
    }: {
      count: number
      isLoading: boolean
    }) =>
      isLoading ? (
        <Skeleton
          variant="text"
          width={40}
          height={48}
          sx={{ fontSize: '2rem' }}
          aria-label="Loading certificate count"
        />
      ) : (
        <Typography variant="h4" className="font-semibold">
          <span aria-live="polite">{count}</span>
        </Typography>
      )

    return (
      <Box className="lg:col-span-1">
        <Card className="w-full flex flex-col h-full">
          <CardContent className="p-8">
            <Typography
              variant="h6"
              className="mb-6 font-semibold"
              id="learning-summary-heading"
            >
              Learning Summary
            </Typography>

            <Box
              className="grid grid-cols-1 gap-4"
              role="region"
              aria-labelledby="learning-summary-heading"
            >
              <Box className="grid grid-cols-[auto_auto_1fr] gap-3 items-center">
                <SchoolIcon sx={{ color: 'primary.main' }} aria-hidden="true" />
                <CertificateCountDisplay
                  count={certificateCounts.recorded}
                  isLoading={selectedUserCertificatesStatus === 'pending'}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  id="recorded-certificates-label"
                  aria-label={`${certificateCounts.recorded} Recorded Certificates`}
                >
                  Recorded Certificates
                </Typography>
              </Box>

              <Box className="grid grid-cols-[auto_auto_1fr] gap-3 items-center">
                <VideoIcon
                  sx={{ color: 'secondary.main' }}
                  aria-hidden="true"
                />
                <CertificateCountDisplay
                  count={certificateCounts.live}
                  isLoading={selectedUserCertificatesStatus === 'pending'}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  id="live-certificates-label"
                  aria-label={`${certificateCounts.live} Live Certificates`}
                >
                  Live Certificates
                </Typography>
              </Box>

              <Box className="grid grid-cols-[auto_auto_1fr] gap-3 items-center">
                <BusinessIcon
                  sx={{ color: 'warning.main' }}
                  aria-hidden="true"
                />
                <CertificateCountDisplay
                  count={certificateCounts.external}
                  isLoading={selectedUserCertificatesStatus === 'pending'}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  id="external-certificates-label"
                  aria-label={`${certificateCounts.external} External Certificates`}
                >
                  External Certificates
                </Typography>
              </Box>

              <Box className="grid grid-cols-[auto_auto_1fr] gap-3 items-center">
                <EventIcon sx={{ color: 'info.main' }} aria-hidden="true" />
                <CertificateCountDisplay
                  count={certificateCounts.event}
                  isLoading={selectedUserCertificatesStatus === 'pending'}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  id="event-certificates-label"
                  aria-label={`${certificateCounts.event} Event Certificates`}
                >
                  Event Certificates
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    )
  },
)

export default LearningSummaryCard
